import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/blocks/page-header';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { TimesheetReminderSettings } from '@/components/TimesheetReminderSettings';
import { ApprovalReminderSettings } from '@/components/ApprovalReminderSettings';
import { MissingTimesheetReminderSettings } from '@/components/MissingTimesheetReminderSettings';
import { ManagerDigestSettings } from '@/components/ManagerDigestSettings';
import { DelegationNotificationSettings } from '@/components/DelegationNotificationSettings';
import type { 
  TimesheetDeadlineSettings as DeadlineSettingsType,
  OrganisationTimesheetSettings
} from '@/types/timesheetSettings';
import { DEFAULT_TIMESHEET_SETTINGS } from '@/types/timesheetSettings';
import type { DelegationSettings } from '@/features/delegation/types/delegation.types';
import { DEFAULT_DELEGATION_SETTINGS } from '@/features/delegation/types/delegation.types';
import { getDelegationService } from '@/features/delegation/services/delegationService';
import { toast } from 'sonner';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { ApiError } from '@/services/apiClient';
import * as settingsRepository from '@/services/settingsRepository';
import { usePermissions } from '@/contexts/PermissionContext';
import { Lock } from 'lucide-react';

interface NotificationsPageProps {
  onNavigate?: (path: string) => void;
}

export const NotificationsPage: React.FC<NotificationsPageProps> = ({ onNavigate }) => {
  const { mode, isDemo, isLive } = useScreenDataMode('settings-notifications');
  const { canManageNotifications } = usePermissions();
  const delegationSvc = getDelegationService(mode);

  const [deadlineSettings, setDeadlineSettings] = useState<DeadlineSettingsType | null>(null);
  const [fullSettings, setFullSettings] = useState<OrganisationTimesheetSettings | null>(null);
  const [rowVersion, setRowVersion] = useState<string | undefined>(undefined);

  const [delegationSettings, setDelegationSettings] = useState<DelegationSettings | null>(null);
  const [isDelegationSettingsValid, setIsDelegationSettingsValid] = useState(true);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSettingsValid, setIsSettingsValid] = useState(true);

  const handleReminderChange = useCallback((settings: DeadlineSettingsType, isValid: boolean) => {
    setDeadlineSettings(settings);
    setIsSettingsValid(isValid);
    setHasUnsavedChanges(true);
  }, []);

  const handleDelegationChange = useCallback((settings: DelegationSettings, isValid: boolean) => {
    setDelegationSettings(settings);
    setIsDelegationSettingsValid(isValid);
    setHasUnsavedChanges(true);
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    // Always reset stale state before fetching so navigating back from
    // General Settings always shows the latest deadline day/time/grace period
    setDeadlineSettings(null);
    setFullSettings(null);

    try {
      if (isDemo) {
        // Demo mode - load from localStorage
        const stored = localStorage.getItem('demo-timesheet-settings');
        if (stored) {
          const settings = JSON.parse(stored) as OrganisationTimesheetSettings;
          setFullSettings(settings);
          setDeadlineSettings(settings.deadline);
        } else {
          // Use defaults
          const defaults = DEFAULT_TIMESHEET_SETTINGS;
          setFullSettings(defaults);
          setDeadlineSettings(defaults.deadline);
        }
        // Load delegation settings (mock or stored)
        const storedDelegation = localStorage.getItem('demo-delegation-settings');
        if (storedDelegation) {
          setDelegationSettings(JSON.parse(storedDelegation) as DelegationSettings);
        } else {
          setDelegationSettings({ ...DEFAULT_DELEGATION_SETTINGS });
        }
      } else if (isLive) {
        // Live mode - fetch both in parallel
        const [settings, delegation] = await Promise.all([
          settingsRepository.getOrganisationTimesheetSettings(mode),
          delegationSvc.getDelegationSettings(),
        ]);
        setFullSettings(settings);
        setRowVersion(settings.rowVersion);
        setDeadlineSettings(settings.deadline);
        setDelegationSettings(delegation);
      }
    } catch (err) {
      console.error('Failed to load notification settings:', err);
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Failed to load notification settings';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [isDemo, isLive, mode, delegationSvc]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Browser warning for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleSaveSettings = async () => {
    if (!deadlineSettings || !fullSettings) return;

    setIsSaving(true);
    try {
      // Use deadlineSettings directly — it is the full settings object kept in sync
      // via handleReminderChange. Do NOT spread fullSettings.deadline on top of it
      // as that would overwrite deadlineDay/deadlineTime with stale values loaded
      // at page open, discarding any changes made in General Settings since then.
      const updatedDeadline = deadlineSettings;

      if (isDemo) {
        // Demo mode - save to localStorage
        const updatedSettings: OrganisationTimesheetSettings = {
          ...fullSettings,
          deadline: updatedDeadline,
        };
        localStorage.setItem('demo-timesheet-settings', JSON.stringify(updatedSettings));
        setFullSettings(updatedSettings);

        if (delegationSettings) {
          localStorage.setItem('demo-delegation-settings', JSON.stringify(delegationSettings));
        }

        toast.success('Notification settings saved successfully');
      } else if (isLive) {
        // Live mode - save deadline and delegation settings in parallel
        const savePromises: Promise<unknown>[] = [
          settingsRepository.updateDeadlineSettings(
            updatedDeadline,
            fullSettings,
            rowVersion,
            mode
          ),
        ];

        if (delegationSettings) {
          savePromises.push(delegationSvc.updateDelegationSettings(delegationSettings));
        }

        const [deadlineResult, delegationResult] = await Promise.all(savePromises) as [
          OrganisationTimesheetSettings & { rowVersion?: string },
          DelegationSettings | undefined,
        ];

        setFullSettings(deadlineResult);
        setRowVersion(deadlineResult.rowVersion);
        // Sync deadlineSettings from the server response so the UI
        // always reflects the authoritative deadline day/time/grace period
        setDeadlineSettings(deadlineResult.deadline);

        if (delegationResult) {
          setDelegationSettings(delegationResult);
        }

        toast.success('Notification settings saved successfully');
      }

      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Failed to save notification settings:', err);
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Failed to save notification settings';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };



  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!canManageNotifications()) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Notification Settings"

          size={36}
        />
        <Alert>
          <Lock className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to access Notification Settings. Contact your administrator to request access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
      <div className="space-y-6">
        {/* Page Header */}
        <PageHeader
          title="Notification Settings"

          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<Bell />}
        />

        {/* Unsaved Changes Alert */}
        {hasUnsavedChanges && (
          <Alert variant="warning">
            <AlertDescription>
              You have unsaved changes. Click "Save Settings" at the bottom to apply your changes.
            </AlertDescription>
          </Alert>
        )}

        {/* Section 1 — Submission Reminders + Missing Timesheet Reminders */}
        {deadlineSettings && (
          <TimesheetReminderSettings
            settings={deadlineSettings}
            onChange={handleReminderChange}
          />
        )}

        {/* Section 2 — Approval Reminders */}
        {deadlineSettings && (
          <ApprovalReminderSettings
            settings={deadlineSettings}
            onChange={handleReminderChange}
          />
        )}

        {/* Section 3 — Missing Timesheet Reminders */}
        {deadlineSettings && (
          <MissingTimesheetReminderSettings
            settings={deadlineSettings}
            onChange={handleReminderChange}
          />
        )}

        {/* Section 4 — Manager Weekly Digest */}
        {deadlineSettings && (
          <ManagerDigestSettings
            settings={deadlineSettings}
            onChange={handleReminderChange}
          />
        )}

        {/* Section 5 — Delegation Notifications */}
        {delegationSettings && (
          <DelegationNotificationSettings
            settings={delegationSettings}
            onChange={handleDelegationChange}
          />
        )}

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button
            onClick={handleSaveSettings}
            disabled={!hasUnsavedChanges || isSaving || !isSettingsValid || !isDelegationSettingsValid}
            size="lg"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>
      </div>
  );
};
